import { useMemo } from "react";
import type { ResizableBoxProps } from "react-resizable";

import NativeQueryEditor from "metabase/query_builder/components/NativeQueryEditor";
import type { SelectionRange } from "metabase/query_builder/components/NativeQueryEditor/types";
import type Question from "metabase-lib/v1/Question";
import type Database from "metabase-lib/v1/metadata/Database";
import type NativeQuery from "metabase-lib/v1/queries/NativeQuery";
import type {
  Database as ApiDatabase,
  NativeQuerySnippet,
} from "metabase-types/api";

/**
 * Variables are deliberately off: their values would have to be persisted per
 * tab, which is a whole feature of its own. Snippets and card references still
 * work.
 */
const SIDEBAR_FEATURES = {
  dataReference: true,
  snippets: true,
  formatQuery: true,
  variables: false,
  promptInput: false,
  aiGeneration: false,
};

export type QueryTabEditorProps = {
  question: Question;
  databases: ApiDatabase[];
  editorHeight: number;
  isRunnable: boolean;
  isRunning: boolean;
  isNativeEditorOpen: boolean;
  isShowingDataReference: boolean;
  isShowingSnippetSidebar: boolean;
  modalSnippet?: NativeQuerySnippet | null;
  nativeEditorSelectedText?: string;
  onChange: (question: Question) => void;
  onRunQuery: () => void;
  onCancelQuery: () => void;
  onResizeStop: (height: number) => void;
  onSetNativeEditorOpen: (isOpen: boolean) => void;
  onToggleEditor: () => void;
  onToggleDataReference: () => void;
  onToggleSnippetSidebar: () => void;
  onInsertSnippet: (snippet: NativeQuerySnippet) => void;
  onChangeModalSnippet: (snippet: NativeQuerySnippet | null) => void;
  onChangeSelection: (range: SelectionRange[]) => void;
};

/**
 * Thin adapter over the query builder's `NativeQueryEditor`.
 *
 * Every assumption this feature makes about that component's (large) prop
 * surface lives in this one file, so an upstream refactor is a single-file fix.
 */
export function QueryTabEditor({
  question,
  databases,
  editorHeight,
  isRunnable,
  isRunning,
  isNativeEditorOpen,
  isShowingDataReference,
  isShowingSnippetSidebar,
  modalSnippet,
  nativeEditorSelectedText,
  onChange,
  onRunQuery,
  onCancelQuery,
  onResizeStop,
  onSetNativeEditorOpen,
  onToggleEditor,
  onToggleDataReference,
  onToggleSnippetSidebar,
  onInsertSnippet,
  onChangeModalSnippet,
  onChangeSelection,
}: QueryTabEditorProps) {
  const resizableBoxProps: Partial<ResizableBoxProps> = useMemo(
    () => ({
      // Height only. The editor collapses by swapping its own `className`
      // between `display: none` and `.open`, and animates `max-height`, so
      // passing `className` or a `transition` here would silently disable
      // collapsing — the spread lands after its own props.
      height: editorHeight,
      onResizeStop: (_event, data) => onResizeStop(data.size.height),
    }),
    [editorHeight, onResizeStop],
  );

  const handleNativeQueryChange = (nativeQuery: NativeQuery) => {
    onChange(nativeQuery.question());
  };

  return (
    <NativeQueryEditor
      question={question}
      query={question.legacyNativeQuery()}
      resizableBoxProps={resizableBoxProps}
      placeholder="SELECT * FROM TABLE_NAME"
      isRunnable={isRunnable}
      isRunning={isRunning}
      isResultDirty={false}
      // Re-syncs rather than forces open: the editor remounts on every tab
      // switch, and a hardcoded `true` would undo a collapsed editor.
      isInitiallyOpen={isNativeEditorOpen}
      isNativeEditorOpen={isNativeEditorOpen}
      setIsNativeEditorOpen={onSetNativeEditorOpen}
      toggleEditor={onToggleEditor}
      hasTopBar
      hasRunButton
      hasParametersList={false}
      readOnly={false}
      isShowingDataReference={isShowingDataReference}
      isShowingSnippetSidebar={isShowingSnippetSidebar}
      handleResize={() => null}
      runQuery={onRunQuery}
      cancelQuery={onCancelQuery}
      setDatasetQuery={handleNativeQueryChange}
      sidebarFeatures={SIDEBAR_FEATURES}
      toggleDataReference={onToggleDataReference}
      toggleSnippetSidebar={onToggleSnippetSidebar}
      modalSnippet={modalSnippet}
      insertSnippet={onInsertSnippet}
      closeSnippetModal={() => onChangeModalSnippet(null)}
      databaseIsDisabled={(database: Database) => {
        const apiDatabase = databases.find((db) => db.id === database.id);
        return apiDatabase?.native_permissions !== "write";
      }}
      setNativeEditorSelectedRange={onChangeSelection}
      nativeEditorSelectedText={nativeEditorSelectedText}
    />
  );
}
